import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { apiClient } from '../../client';
import { isConflictError, isPublishValidationError } from '../../errors';
import {
  processKeys,
  processDraftKeys,
  processLayoutKeys,
  dashboardKeys,
  taskKeys
} from '../../query-keys';
import { invalidateProcessStatus } from '../../process-read-cache';
import { processQueryOptions } from '../../queries/useProcesses';
import { processDraftQueryOptions } from '../../queries/useProcessDraft';
import { seedLayout } from '../../queries/useProcessLayout';
import {
  PublishConflictReason,
  type CreateProcessDraftResponse,
  type DiscardProcessDraftResponse,
  type PublishDraftResponse
} from '@bliks-io/contracts';
import { basePath } from './shared';

/**
 * Targeted cache reset after publish/discard. Process detail is invalidated so
 * the awaited `fetchQuery` in `refetchPublishedProcess` (and `ensureQueryData`
 * in route loaders) refetches via react-query's per-key dedup against the
 * global 5-min staleTime (`lib/query-client.ts`); the `useFlow` position cache
 * is downstream of process detail (keyed off `updatedAt` / step count) and
 * refreshes once it does. The published layout is invalidated as a fallback:
 * `refetchPublishedProcess` force-seeds it fresh via `seedLayout`, but the
 * invalidate guarantees any `published` observer still refetches on mount if
 * that best-effort seed is skipped or fails.
 */
async function onDraftLifecycleSuccess(queryClient: QueryClient, processId: string): Promise<void> {
  queryClient.removeQueries({ queryKey: processDraftKeys.detail(processId) });
  // The draft layout belongs to the just-destroyed draft — leaving Open 1's
  // positions in the cache strands new assignments created on the next draft
  // at the sentinel (ELK won't re-seed them because they look "already saved"
  // against the stale cache).
  queryClient.removeQueries({
    queryKey: processLayoutKeys.detail(processId, 'draft')
  });
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: processKeys.detail(processId) }),
    queryClient.invalidateQueries({
      queryKey: processLayoutKeys.detail(processId, 'published')
    }),
    queryClient.invalidateQueries({ queryKey: dashboardKeys.all }),
    queryClient.invalidateQueries({ queryKey: taskKeys.all })
  ]);
  // Publishing runs the completion checker and re-mints survey rows.
  invalidateProcessStatus(queryClient);
}

/**
 * After a publish/discard, the detail page renders from
 * `processKeys.detail(id)` (step recordings + inputs) and
 * `processLayoutKeys.detail(id, 'published')` (positions). Best-effort warm
 * both BEFORE the caller navigates so the detail page doesn't flash stale data
 * — the published layout via `seedLayout` (cancel + `staleTime: 0`), because a
 * pre-publish GET returning the empty `{ updatedAt: null }` shape would
 * otherwise strand the flow preview on an empty canvas until a manual refresh.
 *
 * Runs inside the mutation's awaited `onSuccess`, so it must never throw: the
 * publish already committed server-side, and a transient refetch failure must
 * not demote it to a "publish failed" error. If a warm fails, the
 * `onDraftLifecycleSuccess` invalidations still make the detail page refetch on
 * mount.
 */
async function refetchPublishedProcess(queryClient: QueryClient, processId: string): Promise<void> {
  try {
    await Promise.all([
      queryClient.fetchQuery(processQueryOptions(processId)),
      seedLayout(queryClient, processId, 'published')
    ]);
  } catch {
    // Best-effort cache warming — the detail page refetches on mount.
  }
}

export async function createProcessDraft(processId: string): Promise<CreateProcessDraftResponse> {
  const { data } = await apiClient.post<CreateProcessDraftResponse>(basePath(processId));
  return data;
}

/**
 * Ensure a draft exists before a draft-backed page mounts. Returns `created:
 * true` when a new draft doc was just POSTed (by us or a racing tab) so the
 * editor loader can force-refresh the layout cache over any parallel GET that
 * landed before the backend's layout seed.
 */
export async function ensureProcessDraft(
  queryClient: QueryClient,
  processId: string
): Promise<{ created: boolean }> {
  const opts = processDraftQueryOptions(processId);
  // fetchQuery, not ensureQueryData: a stale cached draft may have been
  // published or discarded elsewhere since.
  const existing = await queryClient.fetchQuery(opts);
  if (existing !== 'not-found') return { created: false };

  try {
    const data = await createProcessDraft(processId);
    queryClient.setQueryData(processDraftKeys.detail(processId), data);
    return { created: true };
  } catch (err) {
    if (!isConflictError(err)) throw err;
    // Another tab POSTed first. Drop the cached 'not-found' and refetch.
    queryClient.removeQueries({ queryKey: opts.queryKey });
    const refetched = await queryClient.ensureQueryData(opts);
    if (refetched === 'not-found') throw err;
    return { created: true };
  }
}

export function useCreateProcessDraft(processId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => createProcessDraft(processId),
    onSuccess: async (data) => {
      queryClient.setQueryData(processDraftKeys.detail(processId), data);
      await seedLayout(queryClient, processId, 'draft');
    }
  });
}

export function useDiscardProcessDraft(processId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<DiscardProcessDraftResponse> => {
      const { data } = await apiClient.delete<DiscardProcessDraftResponse>(basePath(processId));
      return data;
    },
    onSuccess: async () => {
      await onDraftLifecycleSuccess(queryClient, processId);
      await refetchPublishedProcess(queryClient, processId);
    }
  });
}

/**
 * POST /publish with one automatic retry on 409
 * `draft-modified-during-publish`. The backend races our publish against any
 * in-flight PATCH via an optimistic-concurrency delete; if a PATCH landed
 * between our read and the delete, we get 409, refetch the draft (so the
 * next attempt reads the latest `updatedAt`), and retry once. A second 409
 * surfaces normally — at that point the user is fighting another editor.
 */
async function publishOnce(processId: string, force: boolean): Promise<PublishDraftResponse> {
  const { data } = await apiClient.post<PublishDraftResponse>(
    `${basePath(processId)}/publish`,
    force ? { force: true } : {}
  );
  return data;
}

function isPublishRetryableConflict(error: unknown): boolean {
  if (!isConflictError(error)) return false;
  const err = error as { reason?: string };
  return err.reason === PublishConflictReason.DRAFT_MODIFIED_DURING_PUBLISH;
}

export function usePublishDraft(processId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (
      variables: { force?: boolean } | undefined
    ): Promise<PublishDraftResponse> => {
      const force = variables?.force === true;
      try {
        return await publishOnce(processId, force);
      } catch (error) {
        if (!isPublishRetryableConflict(error)) throw error;
        // Another PATCH slipped in between our initial read and the
        // transactional delete. Refetch the draft so any observers (panels,
        // breadcrumb loaders) see the latest state, then retry once.
        await queryClient.invalidateQueries({
          queryKey: processDraftKeys.detail(processId)
        });
        return await publishOnce(processId, force);
      }
    },
    onError: (error) => {
      // The server validated its own copy of the draft; another tab or
      // co-editor may have changed it since this cache was filled. Refetch so
      // the live validation recomputes from what the server rejected.
      if (!isPublishValidationError(error)) return;
      void queryClient.invalidateQueries({ queryKey: processDraftKeys.detail(processId) });
    },
    onSuccess: async (data) => {
      await onDraftLifecycleSuccess(queryClient, processId);
      await refetchPublishedProcess(queryClient, processId);

      // Publish succeeded but some invite emails couldn't be dispatched
      // (backend returned them per-recipient). Warn the user so they can
      // chase down the affected invitations manually.
      const failed = data.invitesFailed ?? [];
      if (failed.length > 0) {
        toast.warning(t('processDraft.invitesFailedToast', { count: failed.length }));
      }
    }
  });
}
